/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import type { ADNode } from '@atlaskit/editor-common/utils/validator';
import type { akEditorDefaultLayoutWidth as AkEditorDefaultLayoutWidthType } from '@atlaskit/editor-shared-styles/constants';
import EditorEditIcon from '@atlaskit/icon/core/edit';
import { token } from '@atlaskit/tokens';

import { ExtensionSSRMark } from '@confluence/extensions-performance/entry-points/ExtensionSSRMark.ts';
import { ExtensionLoadingComplete } from '@confluence/extensions-performance/entry-points/ExtensionsPerformanceMetrics.ts';
import type { Mode } from '@confluence/macro-tracker/entry-points/types.ts';
import {
	getMacroAttributesFromADFNode,
	getExperienceName,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type { ExcerptIncludeProps } from './types.ts';
import { panel, panelBody, panelHeader, panelHeaderExcerpt } from './styles.ts';
import { i18n } from './i18n.ts';

const akEditorDefaultLayoutWidth = 760 satisfies typeof AkEditorDefaultLayoutWidthType;

type ExcerptIncludeComponentProps = ExcerptIncludeProps & {
	adf?: ADNode | ADNode[];
	pageId: string;
	shouldPanelBeDisplayed: boolean;
	displayInline: boolean;
	excerptName?: string;
	targetContentTitle: string;
	macroId: string;
	mediaToken?: string;
	isLivePage?: boolean;
	renderServerPlaceholder?: boolean;
};

const styles = cssMap({
	excerptInclude: {
		minWidth: '0px',
	},
	inlineExcerptInclude: {
		marginTop: token('space.025'),
	},
	extensionExcerptBody: {
		display: 'block',
		maxWidth: '100%',
	},
	emptyContent: {
		display: 'flex',
		alignItems: 'center',
		padding: token('space.050'),
		color: token('color.text.subtle'),
	},

	fullWidthExtensionWrapper: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		width: `${akEditorDefaultLayoutWidth}px`,
	},
});

// Bodied macros (e.g. Page Properties) render via the platform extension renderer, which sizes the
// body from its measured container width — that collapses to ~2px inside the inline excerpt-include
// wrapper. Plain content (tables, lists, paragraphs) renders fine and is intentionally excluded.
const COLLAPSING_EXTENSION_NODE_TYPES = new Set(['bodiedExtension', 'multiBodiedExtension']);

// The legacy-content migration macro is the only thing that carries nestedContent, and the only way
// a bodied macro ends up inside an excerpt (migrated content).
const LEGACY_CONTENT_EXTENSION_TYPE = 'com.atlassian.confluence.migration';
const LEGACY_CONTENT_EXTENSION_KEY = 'legacy-content';

const isBodiedExtension = (node?: { type?: unknown }): boolean =>
	typeof node?.type === 'string' && COLLAPSING_EXTENSION_NODE_TYPES.has(node.type);

const isLegacyContentExtension = (node?: ADNode): boolean =>
	node?.attrs?.extensionType === LEGACY_CONTENT_EXTENSION_TYPE &&
	node?.attrs?.extensionKey === LEGACY_CONTENT_EXTENSION_KEY;

const excerptContentContainsExtension = (adf?: ADNode | ADNode[]): boolean => {
	if (!adf) {
		return false;
	}

	// Normalise both payload shapes: a content array (macroOutput) or a full `doc` node (resolver).
	let nodes: ADNode[];
	if (Array.isArray(adf)) {
		nodes = adf;
	} else if (adf.type === 'doc' && Array.isArray(adf.content)) {
		nodes = adf.content;
	} else {
		nodes = [adf];
	}

	// Unwrapped bodied macro (view path: macroOutput is already unwrapped).
	if (nodes.some(isBodiedExtension)) {
		return true;
	}

	// Bodied macro still wrapped by the legacy-content migration macro (edit path): look one level
	// into the nestedContent of any legacy-content extension.
	const nestedNodes: ADNode[] = nodes.flatMap((node) =>
		isLegacyContentExtension(node) ? (node.attrs?.parameters?.nestedContent?.content ?? []) : [],
	);
	return nestedNodes.some(isBodiedExtension);
};

export const ExcerptIncludeComponent = ({
	adf,
	contentId,
	pageId,
	shouldPanelBeDisplayed,
	excerptName,
	targetContentTitle,
	mode,
	extensionKey,
	macroNode,
	macroId,
	RendererWrapper,
	isLegacyRenderer,
	mediaToken,
	isLivePage,
	renderServerPlaceholder,
}: ExcerptIncludeComponentProps): JSX.Element => {
	const intl = useIntl();
	const attributes = getMacroAttributesFromADFNode(macroNode);

	/**
	 * Returns true if:
	 * - ADF is non null AND
	 * - IF excerpt is untitled, that its content is not empty
	 * This is because the renderer can return empty ADF for null excerpts
	 */
	const hasExcerpt = (): boolean => {
		const emptyAdf = [{ type: 'paragraph' }];
		return !!adf && (!!excerptName || JSON.stringify(adf) !== JSON.stringify(emptyAdf));
	};

	const getExcerptBody = (): ReactNode => {
		if (!hasExcerpt()) {
			return (
				<ExcerptIncludeEmpty
					mode={mode}
					shouldPanelBeDisplayed={shouldPanelBeDisplayed}
					isLivePage={isLivePage}
				/>
			);
		}

		const forceFullWidthExtension =
			!shouldPanelBeDisplayed &&
			excerptContentContainsExtension(adf) &&
			expValEquals('confluence_frontend_fix_excerpt_include_width', 'isEnabled', true);

		const excerptRenderer = (
			<RendererWrapper
				adf={adf!}
				targetContentId={contentId}
				mediaToken={mediaToken}
				isInsideOfInlineExtension={forceFullWidthExtension ? false : !shouldPanelBeDisplayed}
				headingIdPrefix={
					expValEquals('confluence_toc_included_content_exp', 'isEnabled', true)
						? macroId
						: undefined
				}
			/>
		);

		if (forceFullWidthExtension) {
			return (
				<div css={[styles.extensionExcerptBody, styles.fullWidthExtensionWrapper]}>
					{excerptRenderer}
				</div>
			);
		}

		return excerptRenderer;
	};

	// WS-2563 Adding class to Excerpt Include HTML so it can be excluded from highlights
	const excerptIncludeComponent = (
		<div
			data-fabric-macro={macroId}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className="ak-excerpt-include ak-renderer-extension"
			data-testid="excerpt-include-component"
			css={styles.excerptInclude}
			{...(renderServerPlaceholder
				? { 'data-ssr-placeholder': macroId }
				: { 'data-ssr-placeholder-replace': macroId })}
		>
			{isLegacyRenderer && (
				<ExtensionLoadingComplete
					contentId={pageId}
					extensionKey={extensionKey!}
					node={macroNode}
				/>
			)}
			{shouldPanelBeDisplayed ? (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				<span style={panel}>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */}
					<span style={panelHeader}>
						{targetContentTitle}
						{hasExcerpt() && (
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values, @atlassian/i18n/no-literal-string-in-jsx -- Ignored via go/DSP-18766
							<span style={panelHeaderExcerpt}>
								| {excerptName ?? intl.formatMessage(i18n.untitledExcerpt)}
							</span>
						)}
					</span>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */}
					<span style={panelBody}>{getExcerptBody()}</span>
				</span>
			) : (
				getExcerptBody()
			)}
			<ExtensionSSRMark macroId={macroId} renderType="ssr-new" />
			<MacroExperienceSuccess
				contentId={pageId}
				extensionKey={extensionKey}
				mode={mode}
				attributes={attributes}
				name={getExperienceName(mode, macroNode)}
			/>
		</div>
	);

	return !shouldPanelBeDisplayed ? (
		<div data-testid="inline-excerpt-include-component" css={styles.inlineExcerptInclude}>
			{excerptIncludeComponent}
		</div>
	) : (
		excerptIncludeComponent
	);
};

type ExcerptIncludeEmptyProps = {
	mode: Mode;
	shouldPanelBeDisplayed: boolean;
	isLivePage?: boolean;
};

const ExcerptIncludeEmpty = ({
	mode,
	shouldPanelBeDisplayed,
	isLivePage,
}: ExcerptIncludeEmptyProps) => {
	const intl = useIntl();

	if (!shouldPanelBeDisplayed && !(mode === 'editor' && !isLivePage)) {
		return null;
	}

	return (
		<span css={styles.emptyContent}>
			{intl.formatMessage(i18n.emptyMacro)}
			{mode === 'editor' &&
				!isLivePage &&
				intl.formatMessage(i18n.emptyMacroEdit, {
					editIcon: <EditorEditIcon label="edit" color={token('color.icon')} />,
				})}
		</span>
	);
};
